import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Alert,
    Grid,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    FormControlLabel,
    Switch,
    Autocomplete,
} from '@mui/material';
import {
    Send as SendIcon,
    Email as EmailIcon,
    Person as PersonIcon,
    Groups as GroupsIcon,
    Article as TemplateIcon,
    Preview as PreviewIcon,
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

interface EmailTemplate {
    _id: string;
    name: string;
    subject: string;
    body: string;
    description?: string;
    category: string;
    variables: string[];
    createdAt: string;
}

interface Member {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    membershipType: string;
    membershipStatus: string;
    memberId: string;
}

interface EmailPreview {
    to: string;
    subject: string;
    body: string;
}

const SendEmail: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [members, setMembers] = useState<Member[]>([]);
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Form state
    const [recipientType, setRecipientType] = useState<'all' | 'selected' | 'single'>('all');
    const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
    const [singleEmail, setSingleEmail] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [useTemplate, setUseTemplate] = useState(false);

    // Preview and template dialogs
    const [showPreview, setShowPreview] = useState(false);
    const [showTemplateDialog, setShowTemplateDialog] = useState(false);
    const [showVariablesDialog, setShowVariablesDialog] = useState(false);
    const [preview, setPreview] = useState<EmailPreview | null>(null);

    // Template creation
    const [newTemplate, setNewTemplate] = useState({
        name: '',
        description: '',
        category: 'general',
        subject: '',
        body: '',
    });

    const navigate = useNavigate();

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const [membersResponse, templatesResponse] = await Promise.all([
                apiService.getAllMembers(),
                apiService.getEmailTemplates(),
            ]);

            setMembers(membersResponse.data || []);
            setTemplates(templatesResponse.templates || []);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateSelect = (templateId: string) => {
        const template = templates.find(t => t._id === templateId);
        if (template) {
            setSelectedTemplate(template);
            setSubject(template.subject);
            setBody(template.body);
        }
    };

    const generatePreview = async () => {
        try {
            let previewEmail = singleEmail;
            if (recipientType === 'all') {
                previewEmail = members[0]?.email || 'member@example.com';
            } else if (recipientType === 'selected' && selectedMembers.length > 0) {
                previewEmail = selectedMembers[0].email;
            }

            // Replace variables with sample data for preview
            const sampleVariables = {
                firstName: 'John',
                lastName: 'Doe',
                fullName: 'John Doe',
                memberId: 'GYM001',
                membershipType: 'Home Owner (H/O)',
                membershipStatus: 'Active',
                gymName: 'Legends Gym',
                currentDate: new Date().toLocaleDateString(),
            };

            let previewSubject = subject;
            let previewBody = body;

            // Replace template variables
            Object.entries(sampleVariables).forEach(([key, value]) => {
                const regex = new RegExp(`{{${key}}}`, 'g');
                previewSubject = previewSubject.replace(regex, value);
                previewBody = previewBody.replace(regex, value);
            });

            setPreview({
                to: previewEmail,
                subject: previewSubject,
                body: previewBody,
            });
            setShowPreview(true);
        } catch (err) {
            setError('Failed to generate preview');
        }
    };

    const handleSendEmail = async () => {
        try {
            setLoading(true);
            setError('');

            if (!subject.trim() || !body.trim()) {
                setError('Subject and body are required');
                return;
            }

            // Validate recipient selection
            switch (recipientType) {
                case 'all':
                    // All members are already handled
                    break;
                case 'selected':
                    if (selectedMembers.length === 0) {
                        setError('Please select at least one member');
                        return;
                    }
                    break;
                case 'single':
                    if (!singleEmail.trim()) {
                        setError('Please enter an email address');
                        return;
                    }
                    break;
            }

            const emailData = {
                subject,
                body,
                recipients: recipientType, // 'all', 'selected', or 'single'
                selectedMembers: recipientType === 'selected' ? selectedMembers.map(m => m._id) : [],
                singleEmail: recipientType === 'single' ? singleEmail : '',
                templateId: selectedTemplate?._id,
                variables: {
                    gymName: 'Legends Gym',
                    currentDate: new Date().toLocaleDateString(),
                },
            };

            await apiService.sendEmail(emailData);

            setSuccess(`Email sent successfully to ${getRecipientCount()} recipient(s)`);

            // Reset form
            setSubject('');
            setBody('');
            setSelectedMembers([]);
            setSingleEmail('');
            setSelectedTemplate(null);
            setUseTemplate(false);

        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to send email');
        } finally {
            setLoading(false);
        }
    };

    const saveAsTemplate = async () => {
        try {
            if (!newTemplate.name.trim() || !subject.trim() || !body.trim()) {
                setError('Template name, subject, and body are required');
                return;
            }

            const templateData = {
                ...newTemplate,
                subject,
                body,
            };

            await apiService.saveEmailTemplate(templateData);
            setSuccess('Template saved successfully');
            setShowTemplateDialog(false);

            // Reload templates
            const templatesResponse = await apiService.getEmailTemplates();
            setTemplates(templatesResponse.templates || []);

            // Reset template form
            setNewTemplate({
                name: '',
                description: '',
                category: 'general',
                subject: '',
                body: '',
            });
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save template');
        }
    };

    const getRecipientCount = () => {
        switch (recipientType) {
            case 'all':
                return members.length;
            case 'selected':
                return selectedMembers.length;
            case 'single':
                return singleEmail ? 1 : 0;
            default:
                return 0;
        }
    };

    if (loading && members.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={4}>
                <Button
                    variant="outlined"
                    onClick={() => navigate('/dashboard')}
                >
                    Back to Dashboard
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Recipients Section */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                <GroupsIcon sx={{ mr: 1 }} />
                                Recipients ({getRecipientCount()})
                            </Typography>

                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Recipient Type</InputLabel>
                                <Select
                                    value={recipientType}
                                    onChange={(e) => setRecipientType(e.target.value as any)}
                                    label="Recipient Type"
                                >
                                    <MenuItem value="all">
                                        <Box display="flex" alignItems="center">
                                            <GroupsIcon sx={{ mr: 1 }} />
                                            All Members ({members.length})
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="selected">
                                        <Box display="flex" alignItems="center">
                                            <PersonIcon sx={{ mr: 1 }} />
                                            Selected Members
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="single">
                                        <Box display="flex" alignItems="center">
                                            <EmailIcon sx={{ mr: 1 }} />
                                            Single Email
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            {recipientType === 'selected' && (
                                <Autocomplete
                                    multiple
                                    options={members}
                                    getOptionLabel={(member) => `${member.firstName} ${member.lastName} (${member.email})`}
                                    value={selectedMembers}
                                    onChange={(_, newValue) => setSelectedMembers(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Members"
                                            placeholder="Choose members..."
                                        />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((member, index) => {
                                            const { key, ...tagProps } = getTagProps({ index });
                                            return (
                                                <Chip
                                                    key={member._id}
                                                    label={`${member.firstName} ${member.lastName}`}
                                                    {...tagProps}
                                                    size="small"
                                                />
                                            );
                                        })
                                    }
                                />
                            )}

                            {recipientType === 'single' && (
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={singleEmail}
                                    onChange={(e) => setSingleEmail(e.target.value)}
                                    placeholder="recipient@example.com"
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Template Section */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                                <TemplateIcon sx={{ mr: 1 }} />
                                Email Template
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={useTemplate}
                                        onChange={(e) => setUseTemplate(e.target.checked)}
                                    />
                                }
                                label="Use Template"
                                sx={{ mb: 2 }}
                            />

                            {useTemplate && (
                                <>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Select Template</InputLabel>
                                        <Select
                                            value={selectedTemplate?._id || ''}
                                            onChange={(e) => handleTemplateSelect(e.target.value)}
                                            label="Select Template"
                                        >
                                            {templates.map((template) => (
                                                <MenuItem key={template._id} value={template._id}>
                                                    <Box>
                                                        <Typography variant="body1">{template.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {template.category} • {template.variables.length} variables
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {selectedTemplate && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {selectedTemplate.description}
                                            </Typography>
                                            {selectedTemplate.variables.length > 0 && (
                                                <Box mt={1}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Variables: {selectedTemplate.variables.map(v => `{{${v}}}`).join(', ')}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </>
                            )}

                            <Box display="flex" gap={1}>
                                <Button
                                    variant="outlined"
                                    startIcon={<InfoIcon />}
                                    onClick={() => setShowVariablesDialog(true)}
                                    size="small"
                                >
                                    Variables
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<SaveIcon />}
                                    onClick={() => setShowTemplateDialog(true)}
                                    size="small"
                                >
                                    Save as Template
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Email Content */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Email Content
                            </Typography>

                            <TextField
                                fullWidth
                                label="Subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                sx={{ mb: 2 }}
                                placeholder="Enter email subject..."
                            />

                            <TextField
                                fullWidth
                                label="Message Body"
                                multiline
                                rows={12}
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                placeholder="Enter your email message here...

You can use variables like:
- {{firstName}} for member's first name
- {{lastName}} for member's last name
- {{gymName}} for gym name
- {{currentDate}} for current date"
                                sx={{ mb: 2 }}
                            />

                            <Box display="flex" gap={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    startIcon={<PreviewIcon />}
                                    onClick={generatePreview}
                                    disabled={!subject.trim() || !body.trim()}
                                >
                                    Preview
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                                    onClick={handleSendEmail}
                                    disabled={loading || !subject.trim() || !body.trim() || getRecipientCount() === 0}
                                >
                                    {loading ? 'Sending...' : `Send to ${getRecipientCount()} recipient(s)`}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Preview Dialog */}
            <Dialog open={showPreview} onClose={() => setShowPreview(false)} maxWidth="md" fullWidth>
                <DialogTitle>Email Preview</DialogTitle>
                <DialogContent>
                    {preview && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>To:</Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>{preview.to}</Typography>

                            <Typography variant="subtitle2" gutterBottom>Subject:</Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>{preview.subject}</Typography>

                            <Typography variant="subtitle2" gutterBottom>Message:</Typography>
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                                    {preview.body}
                                </Typography>
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowPreview(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Save Template Dialog */}
            <Dialog open={showTemplateDialog} onClose={() => setShowTemplateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Save as Template</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Template Name"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                        sx={{ mb: 2, mt: 1 }}
                    />

                    <TextField
                        fullWidth
                        label="Description (Optional)"
                        value={newTemplate.description}
                        onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                        sx={{ mb: 2 }}
                        multiline
                        rows={2}
                    />

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={newTemplate.category}
                            onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                            label="Category"
                        >
                            <MenuItem value="general">General</MenuItem>
                            <MenuItem value="membership">Membership</MenuItem>
                            <MenuItem value="billing">Billing</MenuItem>
                            <MenuItem value="event">Event</MenuItem>
                            <MenuItem value="notification">Notification</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
                    <Button onClick={saveAsTemplate} variant="contained">Save Template</Button>
                </DialogActions>
            </Dialog>

            {/* Variables Help Dialog */}
            <Dialog open={showVariablesDialog} onClose={() => setShowVariablesDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Available Variables</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        You can use these variables in your email subject and body. They will be automatically replaced with actual member data:
                    </Typography>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">Member Variables</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List dense>
                                {[
                                    { name: 'firstName', desc: "Member's first name" },
                                    { name: 'lastName', desc: "Member's last name" },
                                    { name: 'fullName', desc: "Member's full name" },
                                    { name: 'email', desc: "Member's email address" },
                                    { name: 'memberId', desc: "Member's ID number" },
                                    { name: 'membershipType', desc: "Type of membership" },
                                    { name: 'membershipStatus', desc: "Current membership status" },
                                ].map((variable) => (
                                    <ListItem key={variable.name}>
                                        <ListItemText
                                            primary={`{{${variable.name}}}`}
                                            secondary={variable.desc}
                                            primaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">Gym Variables</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List dense>
                                {[
                                    { name: 'gymName', desc: "Name of the gym" },
                                    { name: 'currentDate', desc: "Current date" },
                                    { name: 'currentTime', desc: "Current time" },
                                ].map((variable) => (
                                    <ListItem key={variable.name}>
                                        <ListItemText
                                            primary={`{{${variable.name}}}`}
                                            secondary={variable.desc}
                                            primaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </AccordionDetails>
                    </Accordion>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowVariablesDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SendEmail;
